/**
 * Anewall Custom Wallpaper Pricing Calculator
 *
 * Calculates wallpaper pricing based on custom wall dimensions.
 * Base price = the selected variant's price (6 panels — 150"W for Traditional/
 * Pre-pasted, 141"W for Peel & Stick due to 1.5" panel overlap, at 108"H).
 * Larger orders add $75 per panel-equivalent, smaller orders subtract $40.
 * Final price is rounded to the nearest $9 (charm pricing).
 */

(function() {
  'use strict';

  // ============================================================
  // PRICING CONFIGURATION
  // ============================================================
  var CONFIG = {
    panelWidth: 25,        // inches — physical panel width (raw material, all materials)
    panelCoverage: {       // inches of wall coverage per panel (after install overlap)
      traditional: 25,     // no overlap
      prepasted: 25,       // no overlap
      'peel-stick': 23.5   // 25" panel minus 1.5" overlap with neighbor
    },
    standardHeight: 108,   // inches — standard panel height
    standardPanels: 6,     // standard mural = 6 panels
    addRate: 90,           // $ per panel-equivalent added (scaled with June 2026 +20% price raise)
    subtractRate: 48,      // $ per panel-equivalent subtracted (scaled with June 2026 +20% price raise)
    minWidth: 25,
    maxWidth: 1000,
    minHeight: 24,
    maxHeight: 1000
  };

  // ============================================================
  // PRICING FORMULA — matches anewall custom quoting.xlsx exactly
  // ============================================================

  /**
   * Calculate custom wallpaper price.
   *
   * coverage   = panel coverage width for this material (25 or 23.5)
   * panels     = ceil(width / coverage)
   * more/less  = panels - 6
   * extra_h    = height - 108
   * sq_inches  = extra_h * 25 * panels   (physical panel width — material cost)
   * diff       = more/less + (sq_inches / 2700)
   * price      = base + diff * (75 if diff > 0, else 40)
   * Round to nearest $9
   *
   * @param {number} width - Wall width in inches
   * @param {number} height - Wall height in inches
   * @param {number} basePrice - The selected variant's price in cents (6 panels at material-standard width)
   * @param {string} material - Material key ('traditional', 'prepasted', 'peel-stick') — determines panel coverage
   * @returns {object} { price, panels, diff, valid, error }
   */
  function calculatePrice(width, height, basePrice, material) {

    // Validate dimensions
    if (!width || !height || width <= 0 || height <= 0) {
      return { price: basePrice, panels: 1, diff: 0, valid: false, error: 'Enter your wall dimensions' };
    }
    if (width < CONFIG.minWidth) {
      return { price: basePrice, panels: 0, diff: 0, valid: false, error: 'Minimum width is ' + CONFIG.minWidth + '\u2033' };
    }
    if (width > CONFIG.maxWidth) {
      return { price: basePrice, panels: 0, diff: 0, valid: false, error: 'Maximum width is ' + CONFIG.maxWidth + '\u2033' };
    }
    if (height < CONFIG.minHeight) {
      return { price: basePrice, panels: 0, diff: 0, valid: false, error: 'Minimum height is ' + CONFIG.minHeight + '\u2033' };
    }
    if (height > CONFIG.maxHeight) {
      return { price: basePrice, panels: 0, diff: 0, valid: false, error: 'Maximum height is ' + CONFIG.maxHeight + '\u2033' };
    }

    // Step 1: How many panels wide? Use material-specific coverage width.
    // Peel & Stick panels overlap 1.5", so each one only covers 23.5" of wall.
    var coverage = CONFIG.panelCoverage[material] || CONFIG.panelWidth;
    var panelsNeeded = Math.ceil(width / coverage);

    // Step 2: Extra panels beyond the 6-panel standard
    var moreLess = panelsNeeded - CONFIG.standardPanels;

    // Step 3: Extra height beyond standard
    var extraHeight = height - CONFIG.standardHeight;

    // Step 4: Extra area from height change across ALL panels.
    // Uses physical panel width (25") because every panel is 25" of raw material
    // regardless of how much wall it covers after install.
    var sqInches = extraHeight * CONFIG.panelWidth * panelsNeeded;

    // Step 5: Total diff in panel-equivalents
    var standardPanelArea = CONFIG.panelWidth * CONFIG.standardHeight; // 2700
    var diff = moreLess + (sqInches / standardPanelArea);

    // Step 6: Calculate price — $75/panel-eq added, $40/panel-eq subtracted
    var baseDollars = basePrice / 100;
    var rate = (diff > 0) ? CONFIG.addRate : CONFIG.subtractRate;
    var rawPrice = baseDollars + (diff * rate);

    // Step 7: Round to nearest $9 (charm pricing)
    var finalPrice = roundToNearest9(rawPrice);

    return {
      price: Math.round(finalPrice * 100), // Back to cents
      priceFormatted: formatMoney(Math.round(finalPrice * 100)),
      panels: panelsNeeded,
      panelWidth: coverage,    // wall coverage per panel — matches what customer sees
      panelHeight: height,
      diff: diff,
      valid: true,
      error: null
    };
  }

  /**
   * Round a dollar amount to the nearest value ending in 9.
   * e.g., $951.22 -> $959, $387.50 -> $389
   */
  function roundToNearest9(amount) {
    var tens = Math.round(amount / 10) * 10;
    return tens - 1;
  }

  /**
   * Format cents as a dollar string.
   */
  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Normalize a material option value to a config key.
   * e.g., "Traditional - 25\"W x 108\"H" -> "traditional"
   *        "Vinyl / Peel & Stick" -> "peel-stick"
   */
  function normalizeMaterial(materialValue) {
    if (!materialValue) return 'traditional';
    var lower = materialValue.toLowerCase().split(' - ')[0].trim();
    if (lower.indexOf('peel') !== -1 || lower.indexOf('vinyl') !== -1 || lower.indexOf('adhesive') !== -1) {
      return 'peel-stick';
    }
    if (lower.indexOf('prepaste') !== -1 || lower.indexOf('pre-paste') !== -1) {
      return 'prepasted';
    }
    return 'traditional';
  }

  // ============================================================
  // UI CONTROLLER
  // ============================================================

  function initCustomWallpaper() {
    var $container = $('#custom-wallpaper-sizing');
    if (!$container.length) return;

    var $widthInput = $('#custom-wallpaper-width');
    var $heightInput = $('#custom-wallpaper-height');
    var $infoBar = $('#custom-wallpaper-info');
    var $panelCount = $('#custom-wallpaper-panels');
    var $panelDims = $('#custom-wallpaper-panel-dims');
    var $calcPrice = $('#custom-wallpaper-price');
    var $errorMsg = $('#custom-wallpaper-error');
    var $priceDisplay = $('#ProductPrice');
    var $quantitySection = $('.product-single__quantity');

    // Hidden inputs for line item properties
    var $propWidth = $('#custom-prop-width');
    var $propHeight = $('#custom-prop-height');
    var $propMaterial = $('#custom-prop-material');
    var $propPanels = $('#custom-prop-panels');
    var $propColor = $('#custom-prop-color');
    var $propCustomPrice = $('#custom-prop-price');

    // ----------------------------------------------------------
    // Read the selected variant's price (6-panel standard price).
    // This updates when the customer switches materials.
    // ----------------------------------------------------------
    var productJson = null;
    try {
      productJson = JSON.parse($('[id^="ProductJson-"]').text());
    } catch (e) {}

    function getBasePrice() {
      // Try to get the currently selected variant's price
      var $selected = $('select[name="id"], #ProductSelect').find(':selected');
      if ($selected.length && productJson) {
        var selectedId = parseInt($selected.val(), 10);
        for (var i = 0; i < productJson.variants.length; i++) {
          if (productJson.variants[i].id === selectedId) {
            return productJson.variants[i].price;
          }
        }
      }
      // Also check radio-based variant selectors
      var $checkedRadio = $('input[data-parent]:checked');
      if ($checkedRadio.length && productJson) {
        var checkedVal = $checkedRadio.val();
        for (var j = 0; j < productJson.variants.length; j++) {
          var v = productJson.variants[j];
          if (v.title && v.title.indexOf(checkedVal.split(' - ')[0]) !== -1) {
            return v.price;
          }
        }
      }
      // Fallback to product's default price
      if (productJson) return productJson.price;
      // Last resort: parse displayed price
      var priceText = $priceDisplay.text().trim().replace(/[^0-9.]/g, '');
      return Math.round(parseFloat(priceText) * 100) || 0;
    }

    // ----------------------------------------------------------
    // Strip dimension text from WALLPAPER material option labels only.
    // "Traditional - 25"W x 108"H" -> "Traditional"
    // Only targets data-parent="wallpaper" to leave print labels intact.
    // ----------------------------------------------------------
    function cleanMaterialLabels() {
      $('input[data-parent="wallpaper"]').each(function() {
        var $label = $('label[for="' + this.id + '"] .anewall__single-option-text');
        if (!$label.length) return;
        var text = $label.text();
        // Strip dimension suffix like " - 25"W x 108"H" or " - 25W x 108H"
        var cleaned = text.replace(/\s*[-\u2013]\s*\d+["\u201d\u2033]?\s*W\s*x\s*\d+["\u201d\u2033]?\s*H\s*$/i, '').trim();
        if (cleaned && cleaned !== text) {
          $label.text(cleaned);
        }
      });
    }

    // ----------------------------------------------------------
    // Detect wallpaper vs print/tapestry
    // ----------------------------------------------------------
    function isWallpaperSelected() {
      var $checked = $('input[data-child]:checked');
      if ($checked.length) {
        var child = ($checked.data('child') || '').toString().toLowerCase();
        return child === 'wallpaper';
      }
      return $('input[data-parent="wallpaper"]:checked').length > 0;
    }

    function getCurrentMaterial() {
      var $checked = $('input[data-parent]:checked');
      if ($checked.length) {
        return normalizeMaterial($checked.val());
      }
      return 'traditional';
    }

    // ----------------------------------------------------------
    // Numeric input validation for text inputs
    // ----------------------------------------------------------
    function sanitizeNumericInput($input) {
      var val = $input.val().replace(/[^0-9]/g, '');
      if (val !== $input.val()) {
        $input.val(val);
      }
      return parseInt(val, 10) || 0;
    }

    // ----------------------------------------------------------
    // Main calculation + UI update
    // ----------------------------------------------------------
    function updateCalculation() {
      var wallpaperActive = isWallpaperSelected();

      if (!wallpaperActive) {
        $container.hide();
        $quantitySection.show();
        clearLineItemProperties();
        // Don't touch $priceDisplay — let Shopify's variant selector handle it
        return;
      }

      $container.show();
      $quantitySection.hide(); // Qty = 1 always for custom wallpaper

      var width = sanitizeNumericInput($widthInput);
      var height = sanitizeNumericInput($heightInput);
      var material = getCurrentMaterial();

      var result = calculatePrice(width, height, getBasePrice(), material);

      if (result.valid) {
        $errorMsg.hide();
        $infoBar.show();
        $panelCount.text(result.panels + ' panel' + (result.panels !== 1 ? 's' : ''));
        $panelDims.text(result.panelWidth + '\u2033W \u00d7 ' + Math.round(result.panelHeight) + '\u2033H each');
        $calcPrice.text(result.priceFormatted);

        // Update the displayed product price
        $priceDisplay.text(result.priceFormatted);

        // Set line item properties (including calculated price for cart display)
        $propWidth.val(width + ' in');
        $propHeight.val(height + ' in');
        $propMaterial.val(material);
        $propPanels.val(result.panels);
        // Capture selected Color if this product has a Color option
        var $colorChecked = $('input[name="color"]:checked');
        if ($colorChecked.length) {
          $propColor.val($colorChecked.val());
          $propColor.attr('name', 'properties[Color]');
        } else {
          $propColor.val('');
          $propColor.removeAttr('name'); // Don't submit empty Color property
        }
        $propCustomPrice.val(result.price); // cents — used by cart to show correct price
      } else {
        $infoBar.hide();
        if (result.error && (width > 0 || height > 0)) {
          $errorMsg.text(result.error).show();
        } else {
          $errorMsg.hide();
        }

        // Reset price display to base
        $priceDisplay.text(formatMoney(getBasePrice()));
        clearLineItemProperties();
      }
    }

    function clearLineItemProperties() {
      $propWidth.val('');
      $propHeight.val('');
      $propMaterial.val('');
      $propPanels.val('');
      $propColor.val('');
      $propCustomPrice.val('');
    }

    // ----------------------------------------------------------
    // Draft Order checkout via Cloudflare Worker (disabled)
    // TODO: Rebuild as checkout-only intercept for draft order pricing
    // var WORKER_URL = 'https://anewall-checkout.coledpenner.workers.dev';
    // ----------------------------------------------------------

    // ----------------------------------------------------------
    // Event binding
    // ----------------------------------------------------------

    // Clean wallpaper material labels on init (leave print labels alone)
    cleanMaterialLabels();

    // ----------------------------------------------------------
    // Per-material default width: 150" for Traditional/Pre-pasted,
    // 141" for Peel & Stick (6 panels × 23.5" coverage after overlap).
    // ----------------------------------------------------------

    function standardWidthFor(material) {
      var coverage = CONFIG.panelCoverage[material] || CONFIG.panelWidth;
      return CONFIG.standardPanels * coverage;
    }

    // True when the current width matches ANY material's standard,
    // meaning the user hasn't typed a custom value yet.
    function isAtAnyMaterialStandard(width) {
      for (var key in CONFIG.panelCoverage) {
        if (standardWidthFor(key) === width) return true;
      }
      return false;
    }

    // On load: if width is empty or at any material's standard, sync it
    // to the current material's standard (so P&S lands on 141, not 150).
    (function syncWidthOnLoad() {
      var currentVal = parseInt($widthInput.val(), 10);
      if (!currentVal || isAtAnyMaterialStandard(currentVal)) {
        $widthInput.val(standardWidthFor(getCurrentMaterial()));
      }
    })();

    // Dimension input changes
    $widthInput.on('input change', updateCalculation);
    $heightInput.on('input change', updateCalculation);

    // Material, category, or color changes
    $('input[data-parent], input[data-child], input[name="color"]').on('change', function() {
      setTimeout(function() {
        // If width is still at any material's standard (untouched by user),
        // swap to the newly-selected material's standard.
        var currentVal = parseInt($widthInput.val(), 10);
        if (isAtAnyMaterialStandard(currentVal)) {
          $widthInput.val(standardWidthFor(getCurrentMaterial()));
        }
        updateCalculation();
      }, 50);
    });

    // ----------------------------------------------------------
    // Trim confirmation popup — ask before adding custom mural to cart
    // ----------------------------------------------------------
    var trimConfirmed = false;

    function showTrimPopup(onConfirm) {
      if (document.getElementById('trim-confirm-modal')) return;

      var overlay = document.createElement('div');
      overlay.id = 'trim-confirm-modal';
      overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;padding:20px;';

      var modal = document.createElement('div');
      modal.style.cssText = 'background:#fff;max-width:440px;width:100%;padding:32px;border-radius:2px;font-family:"Neuzeit Office",sans-serif;text-align:center;box-shadow:0 10px 40px rgba(0,0,0,0.2);';

      var heading = document.createElement('h3');
      heading.style.cssText = 'font-family:"Arapey",serif;font-size:22px;margin:0 0 16px 0;color:#333;line-height:1.3;';
      heading.textContent = 'Did you add 4\u2033 to your measurements for trimming?';

      var subtext = document.createElement('p');
      subtext.style.cssText = 'font-size:14px;color:#777;margin:0 0 24px 0;line-height:1.5;';
      subtext.textContent = 'Murals are printed slightly larger so you can trim them to fit your wall perfectly';

      var buttonRow = document.createElement('div');
      buttonRow.style.cssText = 'display:flex;gap:12px;justify-content:center;flex-wrap:wrap;';

      var backBtn = document.createElement('button');
      backBtn.type = 'button';
      backBtn.textContent = 'Go Back';
      backBtn.style.cssText = 'background:transparent;border:1px solid #B4ACA8;color:#695f5b;padding:12px 24px;font-family:"Neuzeit Office",sans-serif;font-size:12px;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;min-width:120px;';

      var yesBtn = document.createElement('button');
      yesBtn.type = 'button';
      yesBtn.textContent = 'Yes, Add to Cart';
      yesBtn.style.cssText = 'background:#D0B3A9;border:1px solid #D0B3A9;color:#fff;padding:12px 24px;font-family:"Neuzeit Office",sans-serif;font-size:12px;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;min-width:120px;';

      backBtn.addEventListener('click', function() {
        document.body.removeChild(overlay);
      });

      yesBtn.addEventListener('click', function() {
        document.body.removeChild(overlay);
        onConfirm();
      });

      overlay.addEventListener('click', function(e) {
        if (e.target === overlay) {
          document.body.removeChild(overlay);
        }
      });

      buttonRow.appendChild(backBtn);
      buttonRow.appendChild(yesBtn);
      modal.appendChild(heading);
      modal.appendChild(subtext);
      modal.appendChild(buttonRow);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);
    }

    // Intercept Add to Cart button click for custom-priced wallpaper items
    var $form = $container.closest('form');
    var $addBtn = $form.find('button[name="add"], button[type="submit"][name="add"]');

    $addBtn.on('click', function(e) {
      if (trimConfirmed) return;
      if (!isWallpaperSelected()) return;
      var customPrice = parseInt($propCustomPrice.val(), 10);
      if (!customPrice || customPrice <= 0) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      showTrimPopup(function() {
        trimConfirmed = true;
        $addBtn[0].click();
      });
    });

    // Initial calculation
    updateCalculation();
  }

  // Initialize when DOM is ready
  $(document).ready(function() {
    initCustomWallpaper();
  });

  // Expose for testing
  window.AnewallCustomPricing = {
    calculatePrice: calculatePrice,
    normalizeMaterial: normalizeMaterial,
    CONFIG: CONFIG
  };

})();
